import { useEffect, useMemo, useState } from "react";
import { Loader2, Sliders, Save, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const ROLE_LABELS = { agent: "Agent", supervisor: "Supervisor" };

export default function AgentUIConfig() {
  const [features, setFeatures] = useState([]);
  const [config, setConfig] = useState({ roles: { agent: {}, supervisor: {} } });
  const [groups, setGroups] = useState([]);
  const [groupOverrides, setGroupOverrides] = useState({}); // { [groupId]: {key: bool} }
  const [selectedGroup, setSelectedGroup] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingGroup, setSavingGroup] = useState(false);
  const { canWrite } = useCurrentModulePerms();

  async function load() {
    setLoading(true);
    try {
      const [r, g] = await Promise.all([
        api.get("/admin/agent-ui"),
        api.get("/admin/agent-ui/groups"),
      ]);
      setFeatures(r.data.features || []);
      setConfig(r.data.config || { roles: { agent: {}, supervisor: {} } });
      const gl = g.data.groups || [];
      setGroups(gl);
      const ov = {};
      for (const grp of gl) ov[grp.id] = { ...(grp.feature_flags || {}) };
      setGroupOverrides(ov);
      if (gl.length && !selectedGroup) setSelectedGroup(gl[0].id);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  function toggle(role, key, val) {
    setConfig((c) => ({ ...c, roles: { ...c.roles, [role]: { ...(c.roles?.[role]||{}), [key]: val } } }));
  }

  async function save() {
    setSaving(true);
    try {
      await api.put("/admin/agent-ui", { roles: config.roles });
      toast.success("Agent UI configuration saved");
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }

  // ---- group overrides: tri-state (inherit / on / off) ----
  function setGroupFlag(key, mode) {
    setGroupOverrides((o) => {
      const cur = { ...(o[selectedGroup] || {}) };
      if (mode === "inherit") delete cur[key];
      else cur[key] = mode === "on";
      return { ...o, [selectedGroup]: cur };
    });
  }

  async function saveGroup() {
    if (!selectedGroup) return;
    setSavingGroup(true);
    try {
      await api.put(`/admin/agent-ui/groups/${selectedGroup}`, {
        feature_flags: groupOverrides[selectedGroup] || {},
      });
      toast.success("Group overrides saved");
    } catch (e) { toast.error(formatErr(e)); } finally { setSavingGroup(false); }
  }

  const sections = useMemo(() => {
    const m = {};
    for (const f of features) (m[f.section] ||= []).push(f);
    return Object.entries(m);
  }, [features]);

  const curGroupOv = groupOverrides[selectedGroup] || {};
  const groupOverrideCount = Object.keys(curGroupOv).length;

  if (loading) return <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="agent-ui-title">Agent UI Feature Toggles</h1>
          <p className="text-sm text-muted-foreground mt-1">Enable / disable workspace features per role, then optionally override per user group. Changes propagate live to agents.</p>
        </div>
      </div>

      <Tabs defaultValue="agent">
        <TabsList>
          {Object.keys(ROLE_LABELS).map((r) => (
            <TabsTrigger key={r} value={r} data-testid={`agent-ui-tab-${r}`}>
              {ROLE_LABELS[r]}
              <Badge variant="outline" className="ml-2 text-[10px]">
                {Object.values(config.roles?.[r] || {}).filter(Boolean).length} on
              </Badge>
            </TabsTrigger>
          ))}
          <TabsTrigger value="groups" data-testid="agent-ui-tab-groups">
            <Users className="w-3.5 h-3.5 mr-1.5" /> Groups
          </TabsTrigger>
        </TabsList>

        {/* Role config tabs */}
        {Object.keys(ROLE_LABELS).map((r) => (
          <TabsContent key={r} value={r} className="mt-4">
            <div className="flex justify-end mb-3">
              {canWrite && <Button data-testid="agent-ui-save-btn" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5">
                {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4" />} Save role config
              </Button>}
            </div>
            <Card className="p-2">
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-1">
                {sections.map(([secName, items]) => (
                  <div key={secName} className="py-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Sliders className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-overline text-muted-foreground">{secName}</span>
                    </div>
                    <div className="space-y-1">
                      {items.map((f) => {
                        const v = config.roles?.[r]?.[f.key];
                        const checked = v === undefined ? f.default : !!v;
                        return (
                          <div key={f.key} className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-muted/50">
                            <div className="text-sm">{f.label}</div>
                            <Switch
                              data-testid={`feature-${r}-${f.key}`}
                              checked={checked}
                              onCheckedChange={(val) => toggle(r, f.key, val)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>
        ))}

        {/* Per-group overrides */}
        <TabsContent value="groups" className="mt-4">
          {groups.length === 0 ? (
            <Card className="p-8 text-center text-sm text-muted-foreground">
              No user groups yet. Create groups in Admin → Users & Groups, then override features here.
            </Card>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Group:</span>
                  <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                    <SelectTrigger data-testid="agent-ui-group-select" className="h-9 w-56"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Badge variant="outline" className="text-[10px]">{groupOverrideCount} override{groupOverrideCount === 1 ? "" : "s"}</Badge>
                </div>
                {canWrite && <Button data-testid="agent-ui-group-save-btn" onClick={saveGroup} disabled={savingGroup} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5">
                  {savingGroup ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4" />} Save group overrides
                </Button>}
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Overrides apply on top of the role config for agents in this group. "Inherit" uses the role value.
                When an agent belongs to multiple groups, the most restrictive value wins.
              </p>
              <Card className="p-2">
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-1">
                  {sections.map(([secName, items]) => (
                    <div key={secName} className="py-2">
                      <div className="flex items-center gap-2 mb-2">
                        <Sliders className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-overline text-muted-foreground">{secName}</span>
                      </div>
                      <div className="space-y-1">
                        {items.map((f) => {
                          const raw = curGroupOv[f.key];
                          const mode = raw === undefined ? "inherit" : (raw ? "on" : "off");
                          return (
                            <div key={f.key} className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-muted/50">
                              <div className="text-sm">{f.label}</div>
                              <Select value={mode} onValueChange={(m) => setGroupFlag(f.key, m)}>
                                <SelectTrigger data-testid={`group-feature-${f.key}`} className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="inherit">Inherit</SelectItem>
                                  <SelectItem value="on">On</SelectItem>
                                  <SelectItem value="off">Off</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
